import { useEffect, useState } from "react";
import {
  ButtonSidebarItemProps,
  SubheaderSidebarItemProps,
} from "../../../design/types/sidebar_content";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  ButtonBase,
  Collapse,
  Typography,
  Tooltip,
} from "@mui/material";
import ButtonSidebarItem from "../../../design/high-level/sidebar/sidebar_item_button";
import { XNGIconRenderer, XNGICONS } from "../../../design";
import { useSidebarPalette, SIZE } from "../../../design/high-level/sidebar/_";
import { getSizing } from "../../../design/sizing";
import { useLocation } from "react-router-dom";
import { useXNGSelector } from "../../../context/store";
import { sidebarOpen } from "../../../context/slices/sidebarSlice";
import { readSidebarSnapshot, writeSidebarSnapshot } from "../../../design/high-level/sidebar/sidebar_persistence";

type SubheaderSection = { subheader: string; items: ButtonSidebarItemProps[] };

// Items before the first subheader render flat; each subheader owns the items that follow
// it, up to the next subheader.
function groupBySubheader(children: (ButtonSidebarItemProps | SubheaderSidebarItemProps)[]) {
  const leading: ButtonSidebarItemProps[] = [];
  const sections: SubheaderSection[] = [];
  for (const item of children) {
    if ("subheader" in item) sections.push({ subheader: item.subheader, items: [] });
    else if (sections.length === 0) leading.push(item);
    else sections[sections.length - 1].items.push(item);
  }
  return { leading, sections };
}

export default function SidebarAccordion(props: {
  children: (ButtonSidebarItemProps | SubheaderSidebarItemProps)[],
  onClick: (description: string) => void,
  description: string,
  expanded: boolean
  icon: JSX.Element
  /** Saves which subheaders are open in this browser under the sidebar's key. */
  persistKey?: string
}) {
  const palette = useSidebarPalette();
  const location = useLocation();
  const isSidebarOpen = useXNGSelector(sidebarOpen);
  const { leading, sections } = groupBySubheader(props.children);
  const { persistKey, description } = props;
  const savedPrefix = `${description}/`;

  // Subheaders start collapsed unless saved open; the one holding the current route opens
  // so the active page stays visible.
  const [openSubheaders, setOpenSubheaders] = useState<string[]>(() =>
    persistKey
      ? (readSidebarSnapshot(persistKey)?.subheaders ?? []).flatMap((id) =>
          id.startsWith(savedPrefix) ? [id.slice(savedPrefix.length)] : [],
        )
      : [],
  );
  useEffect(() => {
    if (!persistKey) return;
    const otherGroups = (readSidebarSnapshot(persistKey)?.subheaders ?? []).filter(
      (id) => !id.startsWith(savedPrefix),
    );
    writeSidebarSnapshot(persistKey, {
      subheaders: [...otherGroups, ...openSubheaders.map((s) => savedPrefix + s)],
    });
  }, [persistKey, savedPrefix, openSubheaders]);
  const activeSubheader = sections.find((s) =>
    s.items.some((item) => item.route === location.pathname),
  )?.subheader;
  useEffect(() => {
    if (activeSubheader)
      setOpenSubheaders((open) => (open.includes(activeSubheader) ? open : [...open, activeSubheader]));
  }, [activeSubheader]);

  const toggleSubheader = (subheader: string) =>
    setOpenSubheaders((open) =>
      open.includes(subheader) ? open.filter((s) => s !== subheader) : [...open, subheader],
    );

  const renderItem = (item: ButtonSidebarItemProps) => (
    <ButtonSidebarItem key={item.route} item={item} displayAsSelected={location.pathname === item.route} />
  );

  return (
    <Tooltip title={isSidebarOpen ? "" : props.description} disableInteractive placement="top-start">
      <Accordion
        disableGutters
        sx={{
          mb: 0,
          border: "1px",
          boxShadow: "none",
          "&::before": {
            opacity: "0 !important",
          },
          paddingLeft: "0 !important",
        }}
        onClick={() => props.onClick(props.description)}
        expanded={props.expanded}
      >
        <AccordionSummary
          expandIcon={
            <XNGIconRenderer left color="#4B4B4B" i={<XNGICONS.DownChevron />} size="md" />
          }
          sx={{
            bgcolor: palette.bgcolor,
            display: "flex",
            paddingLeft: "10px"
          }}
        >
          <XNGIconRenderer
            color={palette.selected}
            size={SIZE}
            i={props.icon}
          />
          <Typography color="black" sx={{paddingLeft: getSizing(3)}}>{props.description}</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            bgcolor: palette.bgcolor,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {leading.map(renderItem)}
          {sections.map((section) => {
            const open = openSubheaders.includes(section.subheader);
            return (
              <Box key={section.subheader}>
                <ButtonBase
                  onClick={() => toggleSubheader(section.subheader)}
                  aria-expanded={open}
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingX: getSizing(1),
                    paddingTop: getSizing(1),
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="overline" sx={{ color: palette.deselected }}>
                    {section.subheader}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      transform: open ? "rotate(180deg)" : "none",
                      transition: "transform .2s ease",
                    }}
                  >
                    <XNGIconRenderer color={palette.deselected} i={<XNGICONS.DownChevron />} size="sm" />
                  </Box>
                </ButtonBase>
                <Collapse in={open}>{section.items.map(renderItem)}</Collapse>
              </Box>
            );
          })}
        </AccordionDetails>
      </Accordion>
    </Tooltip>
  )
}
